import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { ToastProvider, TooltipProvider } from '@cyphex/ui';
import { AppOverlays } from '@/features/notifications';
import { RealtimeRuntime } from '@/features/realtime';
/** Deep import so the sign-in pages stay out of the initial chunk. */
import { AuthProvider } from '@/features/auth/context/AuthProvider';
/**
 * Native notifications, tray, badge, global hotkeys, deep links and the native
 * screen-share picker. Deep import, and every hook inside it checks for the
 * shell first — in a browser this mounts nothing and subscribes to nothing.
 */
import { DesktopRuntime } from '@/features/desktop/DesktopRuntime';
import { ShortcutProvider } from './ShortcutProvider';
import { ThemeProvider } from './ThemeProvider';

const STALE_TIME_MS = 30_000;
const RETRY_COUNT = 1;

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * Single mounting point for every app-wide provider.
 * Add new global context providers here — never inside a page.
 */
export function AppProviders({ children }: AppProvidersProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: STALE_TIME_MS,
            retry: RETRY_COUNT,
            refetchOnWindowFocus: false,
          },
          mutations: { retry: 0 },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      {/* Inside QueryClientProvider: signing out clears the whole query cache. */}
      <AuthProvider>
        <ThemeProvider>
          <ShortcutProvider>
            <TooltipProvider delayDuration={200}>
              <ToastProvider>
                <RealtimeRuntime />
                <DesktopRuntime />
                {children}
                <AppOverlays />
              </ToastProvider>
            </TooltipProvider>
          </ShortcutProvider>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
